import { smIconSize } from '@/constants';
import { apiFromAgGridReactRef, useCsvDownloadHandler, useStudyProteoformsCsvDownload } from '@/hooks';
import { useProteoformResultsTableConfig } from '@/hooks/useAssays';
import type { parseFilterModel } from '@/lib/createAgGridDatasource';
import type { SortColumnState } from '@/lib/downloadOptions';
import type { SampleProteoformPublic } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Loading } from '@nautilusbio/nb-design-system/Loading';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { AgGridReact } from 'ag-grid-react';
import { Download } from 'lucide-react';
import React, { useCallback, type FC, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import type { TauSamplesSummary } from './TauSamplesDetailTable';
import { buildTauSamplesDownloadOptions } from './tauSamplesDownloadOptions';
import styles from './TauSamplesSummaryBar.module.css';

interface TauSamplesSummaryBarProps {
  assayId: string;
  orgId: string;
  studyId: string;
  /** The detail grid, so the export carries the sort/filter state actually on screen. */
  gridRef: RefObject<AgGridReact<SampleProteoformPublic> | null>;
  summary: TauSamplesSummary | null;
  hasError?: boolean;
}

const UNAVAILABLE = '—';

function stat(label: string, value: React.ReactNode, testId: string) {
  return (
    <Stack alignItems="center" data-testid={testId} key={testId}>
      <Text as="span" className={styles.statLabel} size="xs">
        {label}
      </Text>
      <Text as="span" size="sm">
        {value}
      </Text>
    </Stack>
  );
}

/** The counts deliberately ignore filters: a fixed frame around a grid that narrows beneath it. */
export const TauSamplesSummaryBar: FC<TauSamplesSummaryBarProps> = ({
  assayId,
  orgId,
  studyId,
  gridRef,
  summary,
  hasError = false,
}) => {
  const { t } = useTranslation('studies');
  const {
    data: tableConfig,
    isError: isTableConfigError,
    isPending: isTableConfigPending,
  } = useProteoformResultsTableConfig(assayId);

  const downloadProteoforms = useStudyProteoformsCsvDownload(orgId, studyId);
  // Non-null because the button below is disabled without a config: it carries the threshold and
  // allow-lists the rows on screen were fetched with, so there is nothing to export against yet.
  const buildOptions = useCallback(
    (columnState: readonly SortColumnState[], filterModel: Parameters<typeof parseFilterModel>[0]) =>
      buildTauSamplesDownloadOptions(columnState, filterModel, tableConfig!, assayId),
    [tableConfig, assayId],
  );
  const handleDownload = useCsvDownloadHandler(
    gridRef,
    apiFromAgGridReactRef,
    buildOptions,
    downloadProteoforms,
    t('study.tauSummary.downloadFailed'),
  );

  // A spinner is an active claim that the value is still coming, so a failure has to end it —
  // otherwise the strip waits forever. Showing 0 instead would read as a real, empty result.
  const count = (value: number | undefined) => {
    if (hasError) return UNAVAILABLE;
    if (!summary || value === undefined) return <Loading type="inline" />;
    return value.toLocaleString();
  };

  return (
    <Stack alignItems="center" gap={5} orientation="horizontal">
      <Button
        data-testid="tau-samples-download-btn"
        isDisabled={!tableConfig}
        isPending={downloadProteoforms.isPending}
        onPress={handleDownload}
        size="md"
        visual="unstyled"
      >
        <Download size={smIconSize} />
        <Text as="span" noWrap size="md">
          {t('study.tauSummary.download')}
        </Text>
      </Button>
      {stat(t('study.tauSummary.totalSamples'), count(summary?.numSamples), 'tau-summary-total-samples')}
      {stat(t('study.tauSummary.proteoforms'), count(summary?.numProteoformGroups), 'tau-summary-proteoforms')}
      {stat(t('study.tauSummary.ptms'), count(summary?.numPtms), 'tau-summary-ptms')}
      {stat(t('study.tauSummary.isoforms'), count(summary?.numIsoforms), 'tau-summary-isoforms')}
      {stat(
        t('study.tauSummary.threshold'),
        isTableConfigPending ? (
          <Loading type="inline" />
        ) : (
          <Stack alignItems="center" gap={1} orientation="horizontal">
            {/* Never fall back to the constant here: this slot claims to be the assay's own
                threshold, and the rows below were filtered by whatever the panel actually said. */}
            <Text as="span" size="sm">
              {isTableConfigError || !tableConfig
                ? UNAVAILABLE
                : t('study.tauSummary.thresholdValue', {
                    value: tableConfig.default_threshold_cpm.toLocaleString(),
                  })}
            </Text>
          </Stack>
        ),
        'tau-summary-threshold',
      )}
    </Stack>
  );
};
